import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { uiActions } from '../../../../store/slices/ui';
import { playerService } from '../../../../services/player';
import useIsMobile from '../../../../utils/isMobile';
import { Pause, Play, Replay, ReplayOne, ShuffleIcon } from '../../../Icons';
import { Reorder, useDragControls } from 'framer-motion';
import './MobileQueueModal.scss';

interface QueueRowProps {
  track: any;
  index: number;
  isEditMode: boolean;
  isSelected: boolean;
  onToggleSelect: (index: number) => void;
  onPlay: (index: number) => void;
}

const QueueRow: FC<QueueRowProps> = memo(({
  track,
  index,
  isEditMode,
  isSelected,
  onToggleSelect,
  onPlay,
}) => {
  const controls = useDragControls();
  const cover = track.album?.images?.[0]?.url || track.images?.[0]?.url || '';
  const artists = track.artists?.map((a: any) => a.name).join(', ') || '';

  return (
    <Reorder.Item
      value={track}
      id={track.uid || `${track.id}-${index}`}
      dragListener={false}
      dragControls={controls}
      className={`mq-track-row ${isSelected ? 'selected' : ''}`}
    >
      {/* Edit Mode Checkbox Circle */}
      {isEditMode && (
        <div
          className='mq-select-cell'
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(index);
          }}
        >
          <div className={`mq-check-circle ${isSelected ? 'checked' : ''}`}>
            {isSelected && (
              <svg role='img' height='12' width='12' viewBox='0 0 24 24' fill='#ffffff'>
                <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Album Cover & Song Details */}
      <div
        className='mq-track-info-cell'
        onClick={() => {
          if (isEditMode) {
            onToggleSelect(index);
          } else {
            onPlay(index);
          }
        }}
      >
        <div className='mq-track-cover-box'>
          {cover ? (
            <img src={cover} alt={track.name} className='mq-track-cover-img' />
          ) : (
            <div className='mq-track-cover-placeholder' />
          )}
        </div>

        <div className='mq-track-meta'>
          <p className='mq-track-title'>{track.name}</p>
          <p className='mq-track-subtitle'>
            <span className='mq-video-tag'>▶ Song</span> • {artists}
          </p>
        </div>
      </div>

      {/* 3 Horizontal Lines Drag Handle */}
      <div
        className='mq-drag-handle-cell'
        onPointerDown={(e) => controls.start(e)}
        aria-label='Drag to reorder'
        title='Drag to reorder'
      >
        <svg role='img' height='20' width='20' viewBox='0 0 24 24' fill='#ffffff'>
          <path d='M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z' />
        </svg>
      </div>
    </Reorder.Item>
  );
});

QueueRow.displayName = 'QueueRow';

export const MobileQueueModal: FC = memo(() => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector((state) => !state.ui.queueCollapsed);
  const currentSong = useAppSelector(
    (state) => state.spotify.state?.track_window.current_track,
    (a, b) => a?.id === b?.id
  );
  const paused = useAppSelector((state) => state.spotify.state?.paused ?? true);
  const shuffle = useAppSelector((state) => state.spotify.state?.shuffle ?? false);
  const repeatMode = useAppSelector((state) => state.spotify.state?.repeat_mode ?? 0);

  const queue = useAppSelector(
    (state) => state.queue.queue,
    (a, b) => {
      if (a === b) return true;
      if (!a || !b) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i]?.id !== b[i]?.id) return false;
      }
      return true;
    }
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [timerMinutes, setTimerMinutes] = useState<number | null>(null);
  const [showTimerPicker, setShowTimerPicker] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset edit state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditMode(false);
      setSelectedIndices([]);
      setShowTimerPicker(false);
    }
  }, [isOpen]);

  if (!isMobile) return null;

  const handleClose = () => {
    dispatch(uiActions.collapseQueue());
  };

  const handleToggleCurrentTrack = () => {
    if (paused) {
      playerService.startPlayback();
    } else {
      playerService.pausePlayback();
    }
  };

  const handlePlayQueueItem = (index: number) => {
    (playerService as any).skipToUpcomingQueueItem(index);
  };

  const handleToggleSelect = (index: number) => {
    setSelectedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleMoveUpSelected = () => {
    if (!selectedIndices.length) return;
    const sorted = [...selectedIndices].sort((a, b) => a - b);
    if (sorted[0] <= 0) return; // Already at top

    for (const idx of sorted) {
      playerService.reorderUpcomingQueue(idx, idx - 1);
    }
    setSelectedIndices(sorted.map((idx) => idx - 1));
  };

  const handleRemoveSelected = () => {
    if (!selectedIndices.length) return;
    const sorted = [...selectedIndices].sort((a, b) => b - a); // descending to preserve indices
    for (const idx of sorted) {
      (playerService as any).removeFromQueue(idx);
    }
    setSelectedIndices([]);
  };

  const handleReorder = (newQueue: any[]) => {
    (playerService as any).setUpcomingQueue(newQueue);
  };

  const toggleShuffle = () => {
    playerService.toggleShuffle(!shuffle).then();
  };

  const toggleRepeat = () => {
    const nextMode = repeatMode === 0 ? 'context' : repeatMode === 1 ? 'track' : 'off';
    playerService.setRepeatMode(nextMode).then();
  };

  const handleSetTimer = (minutes: number | null) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTimerMinutes(minutes);
    setShowTimerPicker(false);

    if (minutes !== null) {
      timerRef.current = setTimeout(() => {
        playerService.pausePlayback();
        setTimerMinutes(null);
      }, minutes * 60 * 1000);
    }
  };

  const currentCover = currentSong?.album?.images?.[0]?.url || '';
  const currentArtists = currentSong?.artists?.map((a) => a.name).join(', ') || '';

  const canMoveUp = selectedIndices.length > 0 && Math.min(...selectedIndices) > 0;
  const canRemove = selectedIndices.length > 0;

  return (
    <div className={`mobile-queue-backdrop ${isOpen ? 'open' : ''}`} onClick={handleClose}>
      <div
        className='mobile-queue-sheet'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Centered Drag Pill Handle */}
        <div className='mq-top-pill-handle' onClick={handleClose} />

        {/* Header Bar */}
        <div className='mq-header-container'>
          {!isEditMode ? (
            <div className='mq-header-normal'>
              <div className='mq-header-text-block'>
                <h1 className='mq-main-title'>Queue</h1>
                <p className='mq-context-subtitle'>Recommended for you</p>
                <div className='mq-device-badge'>
                  <svg role='img' height='13' width='13' viewBox='0 0 24 24' fill='#1db954'>
                    <path d='M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm0 18H7V5h10v14z' />
                  </svg>
                  <span>Wired connection</span>
                </div>
                <p className='mq-promo-text'>
                  <strong>About</strong> recommendations and the impact of promotion
                </p>
              </div>

              <button
                className='mq-edit-pill-btn'
                onClick={() => setIsEditMode(true)}
                aria-label='Edit queue'
              >
                Edit
              </button>
            </div>
          ) : (
            <div className='mq-header-edit'>
              <h1 className='mq-main-title'>Edit queue</h1>
              <button
                className='mq-edit-close-btn'
                onClick={() => {
                  setIsEditMode(false);
                  setSelectedIndices([]);
                }}
                aria-label='Close edit mode'
              >
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Scrollable Song List */}
        <div className='mq-scroll-list'>
          {/* Current Track Row */}
          {currentSong && (
            <div className='mq-now-playing-row' onClick={handleToggleCurrentTrack}>
              <div className='mq-now-playing-cover-box'>
                {currentCover ? (
                  <img src={currentCover} alt={currentSong.name} className='mq-track-cover-img' />
                ) : (
                  <div className='mq-track-cover-placeholder' />
                )}
              </div>

              <div className='mq-now-playing-meta'>
                <p className='mq-now-playing-title'>
                  <span className='mq-green-dots'>... </span>
                  {currentSong.name}
                </p>
                <p className='mq-track-subtitle'>
                  <span className='mq-video-tag'>▶ Song</span> • {currentArtists}
                </p>
              </div>

              <button
                className='mq-play-circle-btn'
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleCurrentTrack();
                }}
                aria-label={paused ? 'Play' : 'Pause'}
              >
                {paused ? <Play /> : <Pause />}
              </button>
            </div>
          )}

          {/* Upcoming Tracks Reorder Group */}
          {queue && queue.length > 0 ? (
            <Reorder.Group
              axis='y'
              values={queue}
              onReorder={handleReorder}
              className='mq-reorder-group'
            >
              {queue.map((track: any, index: number) => (
                <QueueRow
                  key={track.uid || `${track.id}-${index}`}
                  track={track}
                  index={index}
                  isEditMode={isEditMode}
                  isSelected={selectedIndices.includes(index)}
                  onToggleSelect={handleToggleSelect}
                  onPlay={handlePlayQueueItem}
                />
              ))}
            </Reorder.Group>
          ) : (
            <div className='mq-empty-state'>
              <p className='mq-empty-title'>Your queue is empty</p>
              <p className='mq-empty-subtitle'>
                Add songs to play next by tapping '•••' on any track.
              </p>
            </div>
          )}
        </div>

        {/* Bottom Bar: Normal Mode (Shuffle, Repeat, Timer) vs Edit Mode (Move up, Remove) */}
        {!isEditMode ? (
          <div className='mq-footer-controls'>
            <button
              className={`mq-footer-item ${shuffle ? 'active-green' : ''}`}
              onClick={toggleShuffle}
              aria-label='Shuffle'
            >
              <ShuffleIcon />
              <span>Shuffle</span>
            </button>

            <button
              className={`mq-footer-item ${repeatMode !== 0 ? 'active-green' : ''}`}
              onClick={toggleRepeat}
              aria-label='Repeat'
            >
              {repeatMode === 2 ? <ReplayOne active={true} /> : <Replay active={repeatMode === 1} />}
              <span>Repeat</span>
            </button>

            <button
              className={`mq-footer-item ${timerMinutes !== null ? 'active-green' : ''}`}
              onClick={() => setShowTimerPicker((prev) => !prev)}
              aria-label='Sleep timer'
            >
              <svg role='img' height='22' width='22' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M12 2c5.52 0 10 4.48 10 10s-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2zm0 18c4.42 0 8-3.58 8-8s-3.58-8-8-8-8 3.58-8 8 3.58 8 8 8zm.5-13H11v6l5.2 3.1.8-1.3-4.5-2.7V7z' />
              </svg>
              <span>{timerMinutes !== null ? `${timerMinutes}m` : 'Timer'}</span>
            </button>
          </div>
        ) : (
          <div className='mq-edit-bottom-bar'>
            <button
              className={`mq-edit-action-btn ${canMoveUp ? 'enabled' : 'disabled'}`}
              onClick={handleMoveUpSelected}
              disabled={!canMoveUp}
            >
              Move up
            </button>

            <button
              className={`mq-edit-action-btn ${canRemove ? 'enabled' : 'disabled'}`}
              onClick={handleRemoveSelected}
              disabled={!canRemove}
            >
              Remove
            </button>
          </div>
        )}

        {/* Timer Options Picker Popup */}
        {showTimerPicker && (
          <div className='mq-timer-picker-backdrop' onClick={() => setShowTimerPicker(false)}>
            <div className='mq-timer-picker-card' onClick={(e) => e.stopPropagation()}>
              <h3 className='mq-timer-title'>Stop audio in</h3>
              {[5, 15, 30, 45, 60].map((mins) => (
                <button
                  key={mins}
                  className={`mq-timer-option ${timerMinutes === mins ? 'selected' : ''}`}
                  onClick={() => handleSetTimer(mins)}
                >
                  {mins} minutes
                </button>
              ))}
              {timerMinutes !== null && (
                <button
                  className='mq-timer-option turn-off'
                  onClick={() => handleSetTimer(null)}
                >
                  Turn off timer
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

MobileQueueModal.displayName = 'MobileQueueModal';
export default MobileQueueModal;
